import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import AxiosOperations from "../../services/AxiosOperations"
import { API } from "../../util/api"
import { API_STATUS } from "../../util/StaticData"
import { sampleHarmonizationList } from "./dataset/static"
import { setMappedData, setShowMappingSummary } from "./mapping/MapSlice"


interface ScenarioDetails {
  match_type: string;
  row_count: number;
  mapping_percentage: number;
  approval_status: string;
}

interface ColumnMapping {
  source_dataset: string;
  source_columns: string[];
  target_columns: string[];
  target_dataset: string;
}

interface DataHarmonization {
  scenario_details: ScenarioDetails[];
  column_mapped: ColumnMapping[];
}

interface ApiResponse {
  data: {
    scenario_details: ScenarioDetails[];
    column_mapped: ColumnMapping[];
  };
}

interface IDataset {
  data: any[]
  selectedDataset: string
  columns: string[]
  selectedColumns: string[]
}

interface IHarmonizationSliceState {
  user: string
  scenarioList: { [key: string]: any }[]
  isScenarioListLoading: API_STATUS
  datasetList: string[]
  isDatasetListLoading: API_STATUS
  dataset: 1 | 2
  dataset1: IDataset
  dataset2: IDataset
  isDatasetTableLoading: API_STATUS
  scenarioDeleteLoading: API_STATUS
  scenarioMappingStatus: API_STATUS,
  scenarioDetails : any
}

const initialState: IHarmonizationSliceState = {
  user: "",
  scenarioList: [],
  datasetList: [],
  isDatasetListLoading: API_STATUS.IDLE,
  isScenarioListLoading: API_STATUS.IDLE,
  dataset: 1,
  dataset1: {
    data: [],
    selectedDataset: "",
    columns: [],
    selectedColumns: [],
  },
  dataset2: {
    data: [],
    selectedDataset: "",
    columns: [],
    selectedColumns: [],
  },
  scenarioMappingStatus: API_STATUS.IDLE,
  isDatasetTableLoading: API_STATUS.IDLE,
  scenarioDeleteLoading: API_STATUS.IDLE,
  scenarioDetails : []
}

export const getScenarioList = createAsyncThunk(
  "harmonization/getScenarioList",
  async () => {
    try {
      const axiosObj = new AxiosOperations()
      return axiosObj.get(API.HARMONIZATION_GET_SCENARIO_LIST)
      // await new Promise((resolve) => setTimeout(resolve, 500))
      // return sampleHarmonizationList
    } catch (error) {
      console.error("Error:", error)
    }
  },
)

export const getDatasetList = createAsyncThunk(
  "harmonization/getDatasetList",
  async () => {
    try {
      const axiosObj = new AxiosOperations()
      return axiosObj.get(API.HARMONIZATION_GET_DATASET_LIST)
      // await new Promise((resolve) => setTimeout(resolve, 1000))
      // return {
      //   data: ["NSR (Internal Sales Data)", "Nielsen (Survey Sales Data)"],
      // }
    } catch (error) {
      console.error("Error:", error)
    }
  },
)

export const getDatasetTable = createAsyncThunk(
  "harmonization/getDatasetTable",
  async ({
    datasetName,
    datasetCompId,
  }: {
    datasetName: string
    datasetCompId: number
  }) => {
    const params = {
      dataset_name: datasetName,
    }
    try {
      const axiosObj = new AxiosOperations()
      const data = await axiosObj.get(
        API.HARMONIZATION_GET_DATASET_TABLE,
        params,
      )
      // await new Promise((resolve) => setTimeout(resolve, 1000))
      // if (datasetCompId === 1) {
      //   return {
      //     data: {
      //       columns: ["Zone", "Country", "Category", "Brand"],
      //     },
      //     datasetCompId,
      //   }
      // }
      // return {
      //   data: {
      //     columns: ["zone", "country", "category_nie", "brand"],
      //   },
      //   datasetCompId,
      // }
      console.log("HARMONIZATION_GET_DATASET_TABLE",data)
      return {
        data,datasetCompId
      }
    } catch (error) {
      console.error("Error:", error)
    }
  },
)

export const createMappingAsync = createAsyncThunk(
  "harmonization/createMappingAsync",
  async ({ payload }: any) => {
    try {
      const axiosObj = new AxiosOperations()
      const res = await axiosObj.post(API.HARMONIZATION_MAP_COLUMNS, payload)
      return {data:JSON.parse(res.data)}
    } catch (error) {
      console.error("Error:", error)
    }
  },
)

export const getScenarioDetails = createAsyncThunk(
  "harmonization/getScenarioDetails",
  async ({ id }: { id: number }, thunkAPI) => {
    try {
      const axiosObj = new AxiosOperations()
      const res = await axiosObj.get(
        `${API.HARMONIZATION_SCENARIO_DETAILS}?scenario_id=${id}`,
      )
      // AxiosOperations.get returns response.data already
      const apiObj: any = res?.data ?? res

      // API returns top-level { table_data, metadata }
      const tableStr: string = apiObj?.table_data ?? "[]"
      const sanitized = tableStr.replace(/,\s*]$/, "]")
      let rows: any[] = []
      try {
        rows = JSON.parse(sanitized)
      } catch (e) {
        console.error("Failed to parse table_data JSON", e)
        rows = []
      }

      const normalized = rows.map((d: any) => ({
        ...d,
        id: d.id ?? Math.random().toString(36).slice(2, 10),
        status:
          (d?.similarity ?? 0) >= 0.9 ? 1 : (d?.similarity ?? 0) >= 0.8 ? 2 : 3,
      }))

      // Populate mapped data for insights
      thunkAPI.dispatch(setMappedData(normalized))
      // Ensure summary is visible when navigating
      thunkAPI.dispatch(setShowMappingSummary(true))

      return apiObj
    } catch (error) {
      console.error("Error:", error)
      throw error
    }
  },
)

export const deleteMappingAsync = createAsyncThunk(
  "harmonization/deleteMappingAsync",
  async ({ id }: { id: string }) => {
    try {
      const axiosObj = new AxiosOperations()
      return axiosObj.delete(API.HARMONIZATION_DELETE_SCENARIO, {
        scenario_id: id,
      })
    } catch (error) {
      console.error("Error:", error)
    }
  },
)

const harmonizationSlice = createSlice({
  name: "harmonization",
  initialState,
  reducers: {
    setDataset: (state, action) => {
      const { datasetCompId, datasetName: dataset } = action.payload
      datasetCompId === 1
        ? (state.dataset1.selectedColumns = [])
        : (state.dataset2.selectedColumns = [])
      datasetCompId === 1
        ? (state.dataset1.selectedDataset = dataset)
        : (state.dataset2.selectedDataset = dataset)
    },
    setSelectedColumns: (state, action) => {
      const { datasetCompId, selectedColumns } = action.payload
      datasetCompId === 1
        ? (state.dataset1.selectedColumns = selectedColumns)
        : (state.dataset2.selectedColumns = selectedColumns)
    },
    removeSelectedColumn: (state, action) => {
      const { datasetCompId, selectedColumn } = action.payload
      datasetCompId === 1
        ? (state.dataset1.selectedColumns =
            state.dataset1.selectedColumns.filter(
              (col) => col !== selectedColumn,
            ))
        : (state.dataset2.selectedColumns =
            state.dataset2.selectedColumns.filter(
              (col) => col !== selectedColumn,
            ))
    },
    clearDataSets: (state) => {
      state.dataset1 = {
        data: [],
        selectedDataset: "",
        columns: [],
        selectedColumns: [],
      }
      state.dataset2 = {
        data: [],
        selectedDataset: "",
        columns: [],
        selectedColumns: [],
      }
    },
    setUser: (state, action) => {
      const nameArray = action.payload.split(" ")
      state.user =
        nameArray.length === 1
          ? nameArray[0]
          : nameArray[0] + " " + nameArray[nameArray.length - 1][0] + "."
    },

    // temperory reducer for scenario mapping status
    setScenarioMappingStatus: (state, action) => {
      state.scenarioMappingStatus = action.payload
    },

    viewDemoMapping: (state) => {
      state.dataset1.selectedDataset = "NSR (Internal Sales Data)"
      state.dataset2.selectedDataset = "Nielsen (Survey Sales Data)"
      state.dataset1.selectedColumns = [
        "l1.4 Country",
        "L1.5-Category",
        "Brand",
        "Vol (in UC)",
      ]
      state.dataset2.selectedColumns = [
        "country",
        "category_nielsen",
        "Brand",
        "Vol (in UC)",
      ]
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getScenarioList.pending, (state) => {
        state.isScenarioListLoading = API_STATUS.LOADING
      })
      .addCase(getScenarioList.fulfilled, (state, action: any) => {
        state.isScenarioListLoading = API_STATUS.IDLE
        const data = action.payload.data
        const mappedData = data?.map((obj: any) => {
          return {
            accuracy: obj?.accuracy,
            sourceColumns: obj?.source_columns.split(","),
            targetColumns: obj?.target_columns.split(","),
            createdAt: obj?.created_at,
            createdBy: obj?.created_by,
            sourceDataset: obj?.source_dataset,
            targetDataset: obj?.target_dataset,
            executionStatus: obj?.execution_status,
            executionTime: obj?.execution_time,
            id: obj?.id,
          }
        })
        state.scenarioList = mappedData
      })
      .addCase(getScenarioList.rejected, (state) => {
        state.scenarioList = []
        state.isScenarioListLoading = API_STATUS.FAILED
      })
      .addCase(getDatasetList.pending, (state) => {
        state.isDatasetListLoading = API_STATUS.LOADING
      })
      .addCase(getDatasetList.fulfilled, (state, action: any) => {
        state.isDatasetListLoading = API_STATUS.IDLE
        state.datasetList = action.payload.data
      })
      .addCase(getDatasetList.rejected, (state) => {
        state.datasetList = []
        state.isDatasetListLoading = API_STATUS.FAILED
      })
      .addCase(getDatasetTable.pending, (state) => {
        state.isDatasetTableLoading = API_STATUS.LOADING
      })
      .addCase(getDatasetTable.fulfilled, (state, action: any) => {
        state.isDatasetTableLoading = API_STATUS.IDLE
        const { data, datasetCompId } = action.payload

        // const parsedData = JSON.parse(data)
        // const columns = Object.keys(parsedData[0])

        if (datasetCompId === 1) {
          // state.dataset1.data = parsedData
          state.dataset1.columns = data.columns
        }

        if (datasetCompId === 2) {
          // state.dataset2.data = parsedData
          state.dataset2.columns = data.columns
        }
      })
      .addCase(getDatasetTable.rejected, (state) => {
        state.dataset1.data = []
        state.dataset2.data = []
        state.isDatasetTableLoading = API_STATUS.FAILED
      })
      .addCase(createMappingAsync.pending, (state) => {
        state.scenarioMappingStatus = API_STATUS.LOADING
      })
      .addCase(createMappingAsync.fulfilled, (state, action) => {
        state.scenarioMappingStatus = API_STATUS.IDLE
        console.log("created mapping", action.payload)
        // console.log("created mapping data", JSON.parse(action.payload.data))
      })
      .addCase(createMappingAsync.rejected, (state) => {
        state.scenarioMappingStatus = API_STATUS.FAILED
      })
      .addCase(deleteMappingAsync.pending, (state) => {
        state.scenarioDeleteLoading = API_STATUS.LOADING
      })
      .addCase(deleteMappingAsync.fulfilled, (state) => {
        state.scenarioDeleteLoading = API_STATUS.IDLE
      })
      .addCase(deleteMappingAsync.rejected, (state) => {
        state.scenarioDeleteLoading = API_STATUS.FAILED
      })
      .addCase(getScenarioDetails.pending, (state) => {
        state.scenarioMappingStatus = API_STATUS.LOADING
      })
      .addCase(getScenarioDetails.fulfilled, (state, action) => {
        state.scenarioMappingStatus = API_STATUS.IDLE
        console.log("RES OF DETAIL", action.payload)
        const apiData:any = action.payload
        try{
          console.log("[ScenarioDetails.fulfilled] apiData:", apiData)
          const tableJsonStr = apiData?.table_data
          const table = Array.isArray(tableJsonStr) ? tableJsonStr : JSON.parse(tableJsonStr || "[]")
          const metadata = apiData?.metadata || {}
          console.log("[ScenarioDetails.fulfilled] metadata:", metadata)
          console.log("[ScenarioDetails.fulfilled] table:", table)
          // Set dataset names
          state.dataset1.selectedDataset = metadata?.source_dataset || state.dataset1.selectedDataset
          state.dataset2.selectedDataset = metadata?.target_dataset || state.dataset2.selectedDataset

          // Set selected columns from metadata (exact as provided)
          state.dataset1.selectedColumns = Array.isArray(metadata?.source_columns) ? metadata.source_columns : []
          state.dataset2.selectedColumns = Array.isArray(metadata?.target_columns) ? metadata.target_columns : []

          // Derive columns from table data keys
          const keys = table.length ? Object.keys(table[0]) : []
          const excluded = new Set(["id","status","similarity","approval_status","match_status"])        
          const allKeys = keys.filter((k) => !excluded.has(k))
          console.log('[ScenarioDetails.fulfilled] metadata:', metadata)
          console.log('[ScenarioDetails.fulfilled] derived keys:', allKeys)

          // If table is non-suffixed, populate columns exactly from metadata to preserve order; fallback to keys
          state.dataset1.columns = state.dataset1.selectedColumns.length ? state.dataset1.selectedColumns : allKeys
          state.dataset2.columns = state.dataset2.selectedColumns.length ? state.dataset2.selectedColumns : allKeys

          // Stash entire response if needed
          state.scenarioDetails = apiData?.data
        }catch(e){
          console.error("Failed to parse scenario details", e)
        }
      })
      .addCase(getScenarioDetails.rejected, (state) => {
        state.scenarioMappingStatus = API_STATUS.FAILED
      })
  },
})

export const {
  setDataset,
  setSelectedColumns,
  removeSelectedColumn,
  clearDataSets,
  setUser,
  setScenarioMappingStatus,
  viewDemoMapping,
} = harmonizationSlice.actions

export default harmonizationSlice.reducer
